import { ComponentRef, ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  ErrorSummary,
  Field,
  Heading,
  Label,
  List,
  Paragraph,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { RiskMatrixChange, RiskMatrixFactor } from '../../../../api/hms';
import { ApprovalChoice, type ApprovalFormState } from '../../components/ApprovalChoice';
import type { Action } from '../../hmsActions';
import { FIELD_IDS, formatChange, type SendError } from '../riskMatrixView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskMatrix.module.css';


type Props = {
  id: string;
  changes: RiskMatrixChange[];
  factors: RiskMatrixFactor[];
  currentVersion: number;
  changeReason: string;
  onChangeReason: (value: string) => void;
  approval: Omit<ApprovalFormState, 'commentError'>;
  /** Shown only after HR has tried to send */
  errors: SendError[] | null;
  send: Action;
  onDiscard: () => void;
  sendFailed: boolean;
};


export const SendCard = ({
  id,
  changes,
  factors,
  currentVersion,
  changeReason,
  onChangeReason,
  approval,
  errors,
  send,
  onDiscard,
  sendFailed,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const factorName = (key: string) =>
    factors.find((factor) => factor.key === key)?.name ?? t(`hrRiskMatrix.rules.factorNames.${key}`, key);
  const errorFor = (fieldId: string) => errors?.find((error) => error.fieldId === fieldId);
  const reasonError = errorFor(FIELD_IDS.changeReason);
  const commentError = errorFor(FIELD_IDS.approvalComment);

  useEffect(() => {
    if (errors?.length) summaryRef.current?.focus();
  }, [errors]);

  return (
    <section id={id} aria-labelledby={`${id}-heading`} className={layout.card} data-gap="md" data-highlight="true">
      <div className={layout.cardHeading}>
        <Heading level={2} data-size="sm" id={`${id}-heading`}>
          {t('hrRiskMatrix.send.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t('hrRiskMatrix.send.intro', { next: currentVersion + 1, current: currentVersion })}
        </Paragraph>
      </div>
      <div className={classes.changeList}>
        <span className="ds-label" data-size="sm">
          {t('hrRiskMatrix.send.changed')}
        </span>
        <List.Unordered data-size="sm">
          {changes.map((change) => {
            const text = formatChange(change, t, factorName);
            return <List.Item key={text}>{text}</List.Item>;
          })}
        </List.Unordered>
      </div>
      <Field>
        <Label htmlFor={FIELD_IDS.changeReason}>{t('hrRiskMatrix.send.reason')}</Label>
        <Field.Description id={`${FIELD_IDS.changeReason}-description`}>{t('hrRiskMatrix.send.reasonDescription')}</Field.Description>
        <Textarea
          id={FIELD_IDS.changeReason}
          rows={3}
          aria-describedby={`${FIELD_IDS.changeReason}-description`}
          aria-invalid={Boolean(reasonError) || undefined}
          value={changeReason}
          onChange={(event) => onChangeReason(event.target.value)}
        />
        {reasonError && <ValidationMessage>{reasonError.message}</ValidationMessage>}
      </Field>
      <ApprovalChoice
        name="hr-rm-approval"
        legend={t('hms.approval.legend')}
        commentId={FIELD_IDS.approvalComment}
        commentDescription={t('hrRiskMatrix.send.commentDescription')}
        {...approval}
        commentError={commentError?.message}
      />
      {errors && errors.length > 0 && (
        <ErrorSummary ref={summaryRef}>
          <ErrorSummary.Heading>{t('hrRiskMatrix.send.errorSummary')}</ErrorSummary.Heading>
          <ErrorSummary.List>
            {errors.map((error) => (
              <ErrorSummary.Item key={error.fieldId}>
                <ErrorSummary.Link href={`#${error.fieldId}`}>{error.message}</ErrorSummary.Link>
              </ErrorSummary.Item>
            ))}
          </ErrorSummary.List>
        </ErrorSummary>
      )}
      {sendFailed && (
        <Alert data-color="danger">
          <Paragraph data-size="sm">{t('hms.errors.save')}</Paragraph>
        </Alert>
      )}
      <div className={layout.actions}>
        <Button variant="primary" onClick={send.run} loading={send.pending}>
          {approval.mode === 'SAFETY_REP'
            ? t('hrRiskMatrix.send.submit')
            : t('hrRiskMatrix.send.approve', { version: currentVersion + 1 })}
        </Button>
        <Button variant="secondary" onClick={onDiscard}>
          {t('hrRiskMatrix.send.discard')}
        </Button>
      </div>
    </section>
  );
};
